import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip } from "chart.js";
import { formatDecimal } from "../../utils/numberFormat";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

const MARKER_COLOR = "#333333";
const TIE_COLOR = "rgba(35, 188, 237, 0.18)";

// Draws, behind the bars, a band over the places tied with the selected one and, over the bars,
// a line with a triangle on the selected one: with hundreds of bars a bar alone is too thin to see.
const selectedMarker = {
	id: "rankSelectedMarker",
	beforeDatasetsDraw: (chart, args, options) => {
		const selected = options.selected;
		if (!selected || selected.tied === 0) return;
		const { ctx, chartArea, scales: { x } } = chart;
		const half = x.width / chart.data.labels.length / 2;
		ctx.save();
		ctx.fillStyle = TIE_COLOR;
		ctx.fillRect(x.getPixelForValue(selected.first) - half, chartArea.top,
			x.getPixelForValue(selected.last) - x.getPixelForValue(selected.first) + 2 * half, chartArea.bottom - chartArea.top);
		ctx.restore();
	},
	afterDatasetsDraw: (chart, args, options) => {
		const selected = options.selected;
		if (!selected) return;
		const { ctx, chartArea, scales: { x } } = chart;
		// at the start of its tie group, the position of its rank (inside the group the order is just alphabetical)
		const px = Math.round(x.getPixelForValue(selected.first)) + 0.5;
		ctx.save();
		ctx.strokeStyle = MARKER_COLOR;
		ctx.lineWidth = 1.5;
		ctx.beginPath();
		ctx.moveTo(px, chartArea.top + 6);
		ctx.lineTo(px, chartArea.bottom);
		ctx.stroke();
		ctx.fillStyle = MARKER_COLOR;
		ctx.beginPath();
		ctx.moveTo(px - 6, chartArea.top - 2);
		ctx.lineTo(px + 6, chartArea.top - 2);
		ctx.lineTo(px, chartArea.top + 8);
		ctx.closePath();
		ctx.fill();
		ctx.restore();
	}
};

// Every city/neighbourhood as a bar, highest to lowest score, with the selected one marked.
// chartData comes from MapUtils.getRankChartData.
const RankChart = ({ chartData, itemName = "neighbourhood", maxScore, areaName }) => {
	const items = chartData.items || [];
	const highest = items.length && items[0].score !== null ? items[0].score : null;
	const many = items.length > 60;
	const data = {
		labels: items.map(item => item.label),
		datasets: [{
			data: items.map(item => item.score),
			backgroundColor: items.map(item => item.color),
			categoryPercentage: many ? 1 : 0.9,
			barPercentage: many ? 1 : 0.85,
			minBarLength: 2 // a score of 0 still shows as a sliver
		}]
	};
	const options = {
		responsive: true,
		maintainAspectRatio: false,
		animation: items.length > 300 ? false : { duration: 400 },
		layout: { padding: { top: 12, right: 4, left: 6 } },
		interaction: { mode: "index", intersect: false },
		scales: {
			x: { display: false },
			y: {
				beginAtZero: true,
				grid: { color: "rgba(0, 0, 0, 0.06)" },
				border: { display: false },
				ticks: { font: { size: 10 }, maxTicksLimit: 5 }
			}
		},
		plugins: {
			legend: { display: false },
			datalabels: { display: false },
			rankSelectedMarker: { selected: chartData.selected },
			tooltip: {
				displayColors: false,
				callbacks: {
					label: (context) => {
						const score = items[context.dataIndex].score;
						return [
							"Score: " + (score === null ? "no data" : formatDecimal(score, 0, 2)),
							"Rank: " + chartData.ranks[context.dataIndex] + " of " + items.length
						];
					}
				}
			}
		}
	};

	return (
		<div className="chart-container" style={{ width: "388px" }}>
			{(maxScore || highest !== null) &&
				<div className="rank-chart-max">
					{maxScore ? <>Max possible score: <b>{formatDecimal(maxScore, 0, 2)}</b></> : ""}
					{maxScore && highest !== null ? " · " : ""}
					{highest !== null ? <>Highest in {areaName || "this area"}: <b>{formatDecimal(highest, 0, 2)}</b></> : ""}
				</div>}
			<div style={{ position: "relative", height: "220px" }}>
				<Bar data={data} options={options} plugins={[selectedMarker]} />
			</div>
			<div className="rank-chart-axis"><span>Highest</span><span>Lowest</span></div>
			<div className="rank-chart-legend">
				Each bar is one {itemName}, from highest to lowest score. <span className="marker">▼</span> marks the
				selected {itemName}{chartData.selected && chartData.selected.tied > 0 ? <>; the <span className="tie">shaded area</span> shows the {chartData.selected.tied} others with the same score</> : ""}.
			</div>
		</div>
	);
};

export default RankChart;
